// ── Tax Guard integration settings ───────────────────────────────────────────
//
// Where the Tax Guard credentials go. Same shape as the CSC page on purpose —
// one pattern for third-party credentials, so nobody has to learn a second one.
//
// SECRETS ARE WRITE-ONLY HERE. What you type goes to Key Vault and is never read
// back by anything that answers a browser. The boxes show whether a secret is
// set, never what it is.
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { Btn, Card, ErrorMsg, FInput, Field, SectionHeader, Spinner, Td, Th } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";

const PROVIDER = "taxguard";

// Must match SECRETS in api/shared/taxGuardClient.js.
const SECRET_USERNAME = "taxguard-username";
const SECRET_PASSWORD = "taxguard-password";
const SECRET_WEBHOOK  = "taxguard-webhooksecret";

const ENVIRONMENTS = [
  { value: "sandbox",    label: "Sandbox — testing. Orders are not really processed." },
  { value: "production", label: "Production — orders are REAL and billable." },
];

export const IntegrationTaxGuardPage = () => {
  const { api } = useApp();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [row, setRow] = useState(null);

  const [environment, setEnvironment] = useState("sandbox");
  const [isactive, setIsactive] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [webhook, setWebhook] = useState("");

  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState(null);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState(null);
  const [diagging, setDiagging] = useState(false);
  const [diag, setDiag] = useState(null);
  const [syncing, setSyncing] = useState(null);   // null | "dry" | "real"
  const [sync, setSync] = useState(null);

  const load = () => {
    setLoading(true); setErr(null);
    api.get("getIntegrations", { provider: PROVIDER })
      .then(d => {
        setRow(d || null);
        const cfg = (d && d.config) || {};
        setEnvironment(cfg.environment || "sandbox");
        setIsactive(!!(d && d.isactive));
        setLoading(false);
      })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const status = (row && row.secretstatus) || {};
  const have = n => !!status[n];
  const bothSet = have(SECRET_USERNAME) && have(SECRET_PASSWORD);

  // Their documented rule for a password that never expires. Checked as you
  // type, because the alternative is discovering in six months that monitoring
  // has been silently 401ing — which looks exactly like "no findings".
  const pwdNeverExpires = p => p.length >= 32
    && (p.match(/[a-z]/g) || []).length >= 2
    && (p.match(/[A-Z]/g) || []).length >= 2
    && (p.match(/[0-9]/g) || []).length >= 2
    && (p.match(/[^A-Za-z0-9]/g) || []).length >= 2;

  const save = async () => {
    setSaving(true); setNote(null); setTest(null);
    try {
      const secrets = {};
      if (username.trim()) secrets[SECRET_USERNAME] = username.trim();
      if (password)        secrets[SECRET_PASSWORD] = password;   // never trimmed
      if (webhook)         secrets[SECRET_WEBHOOK]  = webhook;

      const res = await api.post("saveIntegration", {
        provider: PROVIDER,
        displayname: "Tax Guard",
        config: {
          environment,
          apiVersion: "v2.14",
          secretnames: [SECRET_USERNAME, SECRET_PASSWORD, SECRET_WEBHOOK],
        },
        secrets,
        isactive,
      });
      setUsername(""); setPassword(""); setWebhook("");
      setRow(r => ({ ...(r || {}), secretstatus: res.secretstatus || {} }));
      setNote("Saved." + (Object.keys(secrets).length
        ? " " + Object.keys(secrets).length + " secret" + (Object.keys(secrets).length === 1 ? "" : "s") + " written to Key Vault."
        : " No secrets changed."));
      load();
    } catch (e) { setNote("Not saved — " + e.message); }
    setSaving(false);
  };

  const runTest = async () => {
    setTesting(true); setTest(null); setDiag(null);
    try { setTest(await api.post("taxGuardTestConnection", {})); }
    catch (e) { setTest({ ok: false, error: e.message }); }
    setTesting(false);
  };

  // Deliberately a SEPARATE button from the connection test. The diagnostic sends
  // unauthenticated probes to both hosts, which is a different kind of thing from
  // "check my credentials" and should be something you chose to do.
  const runDiag = async () => {
    setDiagging(true); setDiag(null);
    try { setDiag(await api.post("taxGuardDiagnose", {})); }
    catch (e) { setDiag({ ok: false, error: e.message }); }
    setDiagging(false);
  };

  // ── Running the sync by hand ──
  //
  // "Nothing should be run automatically, but rather the user/portfolio manager
  // should be able to confirm that things have been run. If they haven't, they
  // should be able to run." This is that control. The scheduler calls the same
  // endpoint with a shared secret; this calls it as you.
  //
  // DRY RUN IS THE DEFAULT ACTION and the real one is deliberately the duller
  // button. A dry run writes nothing and does not advance the watermark, so the
  // first thing anybody does to a new environment is the harmless thing.
  const runSync = async (dryRun) => {
    setSyncing(dryRun ? "dry" : "real"); setSync(null);
    try { setSync({ ...(await api.post("runTaxGuardSync", { dryRun })), _dry: dryRun }); }
    catch (e) { setSync({ ok: false, error: e.message, _dry: dryRun }); }
    setSyncing(null);
  };

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  const inp = { width: "100%", boxSizing: "border-box", padding: "8px 10px", fontSize: 13,
                borderRadius: 6, border: "1px solid #ccc" };
  const chip = ok => ({ fontSize: 11.5, padding: "2px 8px", borderRadius: 4, fontWeight: 600,
                        background: ok ? "#E1F5EE" : "#FBE4DD", color: ok ? "#0F6E56" : "#993C1D" });
  const vaultNote = n => (
    <span style={{ fontSize: 11, color: "var(--color-text-secondary)", marginLeft: 8 }}>Key Vault · {n}</span>
  );

  return <>
    <SectionHeader title="Tax Guard" />

    <Card style={{ padding: 16, marginBottom: 16 }}>
      <div style={{ fontSize: 12.5, color: "var(--color-text-secondary)", marginBottom: 14 }}>
        Tax Guard authenticates with a plain <strong>username and password</strong> — no API key, no
        token exchange. They are specific to an environment: sandbox credentials will not work against
        production. Entered once here, written to Key Vault, never displayed again.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px", maxWidth: 780 }}>
        <div style={{ gridColumn: "1/-1", marginBottom: 12 }}>
          <Field label="Environment">
            <select value={environment} onChange={e => setEnvironment(e.target.value)} style={inp}>
              {ENVIRONMENTS.map(x => <option key={x.value} value={x.value}>{x.label}</option>)}
            </select>
          </Field>
        </div>

        <div>
          <Field label="Username">
            <FInput value={username} onChange={setUsername}
                    placeholder={have(SECRET_USERNAME) ? "•••••••• (leave blank to keep)" : "API username"} />
          </Field>
          <div style={{ marginTop: -8, marginBottom: 10 }}>
            <span style={chip(have(SECRET_USERNAME))}>{have(SECRET_USERNAME) ? "set" : "not set"}</span>
            {vaultNote(SECRET_USERNAME)}
          </div>
        </div>

        <div>
          <Field label="Password">
            <FInput value={password} onChange={setPassword}
                    placeholder={have(SECRET_PASSWORD) ? "•••••••• (leave blank to keep)" : "API password"} />
          </Field>
          <div style={{ marginTop: -8, marginBottom: 10 }}>
            <span style={chip(have(SECRET_PASSWORD))}>{have(SECRET_PASSWORD) ? "set" : "not set"}</span>
            {vaultNote(SECRET_PASSWORD)}
          </div>
        </div>

        {/* ── THE SIX-MONTH TRAP ──
            Tax Guard expires passwords every 6 months by default. For an API
            credential that means monitoring starts failing with a 401 months
            after anyone last touched it — and a monitoring feed that returns
            nothing looks identical to a borrower with nothing to report. Their
            documented escape is a long, complex password that never expires, so
            the rule is checked here, while somebody is pasting one in. */}
        {password && (
          <div style={{ gridColumn: "1/-1", marginTop: -4, marginBottom: 10 }}>
            <div style={{ fontSize: 12, borderRadius: 8, padding: "8px 12px",
                          background: pwdNeverExpires(password) ? "#E1F5EE" : "#FAEEDA",
                          border: "0.5px solid " + (pwdNeverExpires(password) ? "#A8D5C5" : "#EBD3A0"),
                          color: pwdNeverExpires(password) ? "#0F6E56" : "#7a5200" }}>
              {pwdNeverExpires(password)
                ? "This password meets Tax Guard's rule for one that never expires."
                : "This password will EXPIRE IN SIX MONTHS. Tax Guard does not expire a password of 32+ "
                  + "characters with 2+ lowercase, 2+ uppercase, 2+ special characters and 2+ digits. "
                  + "An expired credential fails as a 401, and a monitoring feed returning nothing looks "
                  + "exactly like a borrower with nothing to report."}
            </div>
          </div>
        )}

        <div>
          <Field label="Webhook signing secret (optional)">
            <FInput value={webhook} onChange={setWebhook}
                    placeholder={have(SECRET_WEBHOOK) ? "•••••••• (leave blank to keep)" : "Shared secret for report-status callbacks"} />
          </Field>
          <div style={{ marginTop: -8, marginBottom: 10 }}>
            <span style={chip(have(SECRET_WEBHOOK))}>{have(SECRET_WEBHOOK) ? "set" : "not set"}</span>
            {vaultNote(SECRET_WEBHOOK)}
          </div>
          <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: -6 }}>
            Tax Guard signs report-status callbacks with this (HMAC-SHA256, sent as X-TaxGuard-Signature).
            Without it an inbound callback is an unauthenticated claim that a report is ready.
          </div>
        </div>

        <div style={{ alignSelf: "center" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={isactive} onChange={e => setIsactive(e.target.checked)} />
            Enabled — ACE may call Tax Guard
          </label>
          <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 4 }}>
            Off by default. Nothing calls Tax Guard while this is off, including the test below.
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 16 }}>
        <Btn variant="primary" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</Btn>
        <Btn disabled={testing || !bothSet || !isactive} onClick={runTest}>
          {testing ? "Testing…" : "Test connection"}
        </Btn>
        {/* Needs neither credentials nor the integration switched on — the whole
            point is that it works when the connection test cannot. */}
        <Btn disabled={diagging} onClick={runDiag}>
          {diagging ? "Probing…" : "Diagnose 401 / 403"}
        </Btn>
        <Btn disabled={!!syncing || !bothSet || !isactive} onClick={() => runSync(true)}>
          {syncing === "dry" ? "Running…" : "Dry run sync"}
        </Btn>
        <Btn disabled={!!syncing || !bothSet || !isactive} onClick={() => runSync(false)}>
          {syncing === "real" ? "Running…" : "Run sync"}
        </Btn>
        {!bothSet && <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
          A username and password have to be saved before the connection can be tested.
        </span>}
        {bothSet && !isactive && <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
          Enable the integration to test it.
        </span>}
      </div>

      {note && <div style={{ fontSize: 12.5, marginTop: 12, background: "var(--color-background-secondary)",
                             border: "0.5px solid var(--color-border-tertiary)", borderRadius: 8,
                             padding: "8px 12px" }}>{note}</div>}
    </Card>

    {test && (
      <Card style={{ padding: 16, marginBottom: 16,
                     borderLeft: "3px solid " + (test.ok ? "#0F6E56" : "#993C1D") }}>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>
          {test.ok ? "Connected" : "Could not connect"}
          {typeof test.ms === "number" && (
            <span style={{ fontWeight: 400, fontSize: 12, color: "var(--color-text-secondary)", marginLeft: 8 }}>
              {test.ms} ms
            </span>
          )}
        </div>
        {test.ok ? <>
          <div style={{ fontSize: 13, marginBottom: 10 }}>
            Authenticated against <strong>{test.label}</strong> on API {test.apiVersion} ·{" "}
            <strong>{test.designeeCount}</strong> designee{test.designeeCount === 1 ? "" : "s"}
            {typeof test.clientCount === "number" && <> · <strong>{test.clientCount}</strong> client{test.clientCount === 1 ? "" : "s"} already enrolled</>}.
          </div>
          {test.clientNote && (
            <div style={{ fontSize: 12, color: "#7a5200", background: "#FAEEDA", border: "0.5px solid #EBD3A0",
                          borderRadius: 8, padding: "8px 12px", marginBottom: 10 }}>{test.clientNote}</div>
          )}
          {!!(test.sample || []).length && (
            <table style={{ borderCollapse: "collapse", fontSize: 12.5 }}>
              <thead><tr><Th>Designee</Th><Th>City</Th><Th>State</Th><Th>CAF</Th><Th>Status</Th></tr></thead>
              <tbody>{test.sample.map((d, i) => (
                <tr key={i}>
                  <Td>{d.name}</Td><Td>{d.city}</Td><Td>{d.state}</Td>
                  <Td><span style={{ fontFamily: "var(--font-mono)", fontSize: 11.5 }}>{d.caf}</span></Td>
                  <Td>{d.status}</Td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </> : <>
          <div style={{ fontSize: 13, color: "#993C1D" }}>{test.error}</div>

          {/* ── WHO SAID NO ──
              A status line and nothing else sends you to retype a password that
              was never the problem. 401 is the credentials; 403 means they were
              accepted and something further along refused; an EMPTY body on
              either is not Tax Guard at all, because every documented error of
              theirs carries JSON. The host is shown because the sandbox
              hostname is still an assumption on our side. */}
          {(test.httpStatus || test.where) && (
            <div style={{ marginTop: 10, fontSize: 12, lineHeight: 1.7,
                          background: "var(--color-bg-subtle, #F6F6F5)", borderRadius: 8,
                          padding: "8px 12px", fontFamily: "var(--font-mono)" }}>
              {test.httpStatus && <div>HTTP {test.httpStatus}
                {test.bodyEmpty === true  && <span style={{ color: "#993C1D" }}> · empty body — the application probably never saw it</span>}
                {test.bodyEmpty === false && <span> · answered with a body</span>}
              </div>}
              {test.where && <div>{test.where.label} · {test.where.base}</div>}
              {test.where && <div>Accept: {test.where.accept}</div>}
              {!!(test.errorKeys || []).length && <div>keys: {test.errorKeys.join(", ")}</div>}
              {test.respHeaders && Object.keys(test.respHeaders).map(k => (
                <div key={k}>{k}: {test.respHeaders[k]}</div>
              ))}
            </div>
          )}
        </>}
      </Card>
    )}

    {/* ── Sync result ── */}
    {sync && (
      <Card style={{ padding: 16, marginBottom: 16,
                     borderLeft: "3px solid " + (sync.ok === false ? "#993C1D" : sync._dry ? "#7a5200" : "#0F6E56") }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
          {sync._dry ? "Dry run — nothing was written" : "Sync run"}
          {typeof sync.ms === "number" && (
            <span style={{ fontWeight: 400, color: "var(--color-text-secondary)", marginLeft: 8 }}>{sync.ms} ms</span>
          )}
        </div>

        {sync.ok === false ? (
          <div style={{ fontSize: 13, color: "#993C1D" }}>{sync.error}</div>
        ) : <>
          <div style={{ fontSize: 12.5, lineHeight: 1.7, marginBottom: 10 }}>
            Window from <strong>{(sync.windowStart || "").slice(0, 19).replace("T", " ")}</strong> ·{" "}
            <strong>{sync.discovered}</strong> report{sync.discovered === 1 ? "" : "s"} discovered ·{" "}
            <strong>{sync.stored}</strong> stored · <strong>{sync.alreadyHad}</strong> already held ·{" "}
            <strong>{sync.unmatched}</strong> unmatched · <strong>{sync.failed}</strong> failed
            {!!sync.liabilities && <> · <strong>{sync.liabilities}</strong> liabilities</>}
            {!!sync.newLiens && <> · <strong style={{ color: "#993C1D" }}>{sync.newLiens} NEW LIEN(S)</strong></>}
          </div>

          {/* ── The expected first-run outcome, said before it looks like a bug ──
              Clients enrolled through Tax Guard's portal carry no ACE ids, so
              nothing matches until they are mapped. The sync refuses to guess:
              attaching a federal tax lien to the wrong company by fuzzy-matching
              a name is not a risk worth any amount of convenience. */}
          {sync.unmatched > 0 && sync.stored === 0 && (
            <div style={{ fontSize: 12.5, lineHeight: 1.6, background: "#FAEEDA",
                          border: "0.5px solid #EBD3A0", borderRadius: 8,
                          padding: "8px 12px", marginBottom: 10, color: "#7a5200" }}>
              <strong>Everything is unmatched, and on a first run that is expected.</strong> Clients
              enrolled through Tax Guard's own portal carry no ACE identifiers, so there is nothing
              to match on yet. The fix is to map them — not to loosen the matching. Nothing is
              guessed from a company name on purpose: a near-miss would file somebody else's federal
              tax lien against one of your borrowers.
            </div>
          )}

          {!!(sync.notes || []).length && (
            <div style={{ fontSize: 11.5, fontFamily: "var(--font-mono)", lineHeight: 1.6,
                          background: "var(--color-bg-subtle, #F6F6F5)", borderRadius: 8,
                          padding: "8px 12px", maxHeight: 280, overflowY: "auto" }}>
              {sync.notes.map((n, i) => <div key={i}>{n}</div>)}
            </div>
          )}
        </>}
      </Card>
    )}

    {/* ── Diagnostics ──
        One table and one sentence. The sentence is the output; the table is the
        evidence, in a shape you can paste into an email to Tax Guard support. */}
    {diag && (
      <Card style={{ padding: 16, marginBottom: 16, borderLeft: "3px solid #7a5200" }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
          Who is refusing us
          {typeof diag.ms === "number" && (
            <span style={{ fontWeight: 400, color: "var(--color-text-secondary)", marginLeft: 8 }}>
              {diag.ms} ms
            </span>
          )}
        </div>

        {!diag.ok ? (
          <div style={{ fontSize: 13, color: "#993C1D" }}>{diag.error}</div>
        ) : <>
          <div style={{ fontSize: 12.5, lineHeight: 1.6, background: "#FAEEDA",
                        border: "0.5px solid #EBD3A0", borderRadius: 8,
                        padding: "8px 12px", marginBottom: 12, color: "#7a5200" }}>
            {diag.verdict}
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead><tr>
              <Th>Probe</Th><Th>Auth</Th><Th>Status</Th><Th>Body</Th><Th>server</Th>
            </tr></thead>
            <tbody>
              {(diag.rows || []).map((r, i) => (
                <tr key={i}>
                  <Td>{r.label}
                    <div style={{ fontSize: 10.5, color: "var(--color-text-secondary)",
                                  fontFamily: "var(--font-mono)" }}>{r.url}</div>
                  </Td>
                  <Td>{r.skipped ? "—" : (r.authenticated ? "yes" : "none")}</Td>
                  <Td>
                    {r.skipped ? <span style={{ color: "var(--color-text-secondary)" }}>skipped</span>
                      : r.error ? <span style={{ color: "#993C1D" }}>no reply</span>
                      /* For an ANONYMOUS probe 401 is the good outcome — it proves the
                         login layer is reachable. For an AUTHENTICATED one it is a
                         failure and 200 is the good outcome. Same number, opposite
                         meaning, so the colour has to follow which probe it was. */
                      : <span style={chip(r.authenticated ? r.status < 400 : r.status === 401)}>{r.status}</span>}
                  </Td>
                  <Td>{r.skipped || r.error ? "—"
                       : r.status < 400 ? <span>{typeof r.count === "number" ? r.count + " record(s)" : "ok"}</span>
                       : (r.bodyEmpty ? "empty" : "JSON/text")}</Td>
                  <Td style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>
                    {(r.headers && r.headers.server) || "—"}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>

          {(diag.rows || []).some(r => r.skipped) && (
            <div style={{ fontSize: 11.5, color: "var(--color-text-secondary)", marginTop: 8 }}>
              {(diag.rows.find(r => r.skipped) || {}).skipped}
            </div>
          )}
          {(diag.rows || []).some(r => r.snippet) && (
            <div style={{ marginTop: 10, fontSize: 11, fontFamily: "var(--font-mono)",
                          background: "var(--color-bg-subtle, #F6F6F5)", borderRadius: 8,
                          padding: "8px 12px", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
              {diag.rows.filter(r => r.snippet).map((r, i) => (
                <div key={i} style={{ marginBottom: 6 }}>{r.label}: {r.snippet}</div>
              ))}
            </div>
          )}
        </>}
      </Card>
    )}

    <Card style={{ padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>What this does, and does not, allow</div>
      <div style={{ fontSize: 12.5, color: "var(--color-text-secondary)", lineHeight: 1.6 }}>
        Enabling Tax Guard lets ACE <strong>read</strong>: enrolled clients, the state of each borrower's
        Form 8821, reports that have completed, and their findings — liens, levy notices, missing returns,
        installment agreements. A scheduled job may do that, because reading costs nothing and changes
        nothing.
        <br />
        It does <strong>not</strong> let anything create a client, place an order or close one. An order is
        billable and sends an IRS request under a borrower's signed authorization, so it happens only when
        a person asks — enforced in the code, not just in policy: the scheduled path physically has no
        method that can place one.
        <br /><br />
        <span style={{ color: B.cyanDark, fontWeight: 600 }}>Nothing is ordered automatically. Ever.</span>
        <br /><br />
        Note that a borrower cannot be monitored at all without a <strong>Form 8821</strong> they have
        signed, and an 8821 expires. ACE tracks that expiry separately, because monitoring does not fail
        loudly when authorization lapses — it simply stops finding anything.
      </div>
    </Card>
  </>;
};
